import React, { useMemo } from 'react';

import CategoryListItem from 'components/CategoryList/CategoryListItem';

import classNames from 'classnames';

import type { DraggableSyntheticListeners } from '@dnd-kit/core';
import type { Transform } from '@dnd-kit/utilities';
import { useCategoryContext } from 'components/CategoryList/CategoryContext';
import { useVenueContext } from 'routes/Main';

import { Permission, Product } from 'generated/graphql';
import styles from './Item.module.scss';

export interface Props {
  dragging?: boolean; // eslint-disable-line react/require-default-props, react/no-unused-prop-types
  transform?: Transform | null; // eslint-disable-line react/require-default-props, react/no-unused-prop-types
  listeners?: DraggableSyntheticListeners; // eslint-disable-line react/require-default-props, react/no-unused-prop-types
  transition?: string | null; // eslint-disable-line react/require-default-props, react/no-unused-prop-types
  wrapperStyle?: React.CSSProperties; // eslint-disable-line react/require-default-props, react/no-unused-prop-types
  item: Product; // eslint-disable-line react/require-default-props, react/no-unused-prop-types
  isLoading?: boolean; // eslint-disable-line react/require-default-props, react/no-unused-prop-types
  isDragOverlay?: boolean; // eslint-disable-line react/require-default-props, react/no-unused-prop-types
  categoryId: string; // eslint-disable-line react/require-default-props, react/no-unused-prop-types
}

const Item = React.memo(
  React.forwardRef<HTMLLIElement, Props>(
    (
      {
        dragging,
        listeners,
        transition,
        transform,
        wrapperStyle,
        item,
        isLoading,
        isDragOverlay,
        categoryId,
        ...props
      },
      ref
    ) => {
      const { apps, collapsedCategories, changedProductIds } = useCategoryContext();
      const { currentUser } = useVenueContext();
      const isOpen = useMemo(() => {
        if (collapsedCategories.includes(categoryId)) return false;
        return true;
      }, [categoryId, collapsedCategories]);
      const canEditItem = currentUser?.permissions?.includes(Permission.PublishMenu);

      return (
        <li
          data-testid="draggable-item"
          className={classNames(styles.Wrapper)}
          aria-label={`Press space to drag ${item.name}, or shift + space on MacOS with Full Keyboard Access`}
          style={
            {
              ...wrapperStyle,
              transition: [transition, wrapperStyle?.transition].filter(Boolean).join(', '),
              '--translate-x': transform ? `${Math.round(transform.x)}px` : undefined,
              '--translate-y': transform ? `${Math.round(transform.y)}px` : undefined,
              '--scale-x': transform?.scaleX ? `${transform.scaleX}` : undefined,
              '--scale-y': transform?.scaleY ? `${transform.scaleY}` : undefined
            } as React.CSSProperties
          }
          ref={ref}
        >
          <div
            className={classNames(styles.Item, dragging && styles.Dragging, isDragOverlay && styles.DragOverlay)}
            {...(canEditItem ? listeners : {})}
            {...props}
          >
            <CategoryListItem
              menuItem={item}
              isOpen={isOpen}
              isLoading={isLoading}
              apps={apps}
              categoryId={categoryId}
              modified={!isLoading && changedProductIds.includes(item.mmId)}
            />
          </div>
        </li>
      );
    }
  )
);
Item.displayName = 'Item';

export { Item };
